import { Alert, Button, Form, Input, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat, dateTimeFormat } from "@/utils";
import type { InvoiceVo } from "@zhihaoscm/entity";
import { RECEIVE_PAYMENT_TYPE } from "@zhihaoscm/meta";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    Table as ZhTable,
    Upload as ZhUpload,
    type UploadRequestOptions,
    type UploadUserFile,
    type TableColumnProps
} from "zhihao-ui-react";

const invoiceStateLabels: Record<number, string> = {
    1: "已开票",
    2: "已作废"
};

interface RelatedReceiveInfoItem {
    receivePaymentId: number | string;
    invoiceAmount: number;
}

const paymentTypeLabels: Record<number, string> = {
    1: RECEIVE_PAYMENT_TYPE.ORDER_DEPOSIT.intro,
    2: RECEIVE_PAYMENT_TYPE.ADVANCE_PAYMENT.intro,
    3: RECEIVE_PAYMENT_TYPE.SHIPPING_PAYMENT.intro,
    4: RECEIVE_PAYMENT_TYPE.WAREHOUSE_OPENING_PAYMENT.intro,
    5: RECEIVE_PAYMENT_TYPE.ORDER_PAYMENT.intro
};

/** 将 Vue 详情的日期字段按 dateFormat 默认格式展示到日期。 */
function formatDate(value: unknown): string {
    if (!value) return "-";
    return dateFormat(String(value));
}

/** 将 Vue 详情的日期时间字段按 DATETIME_PICKER_FORMAT 展示到分钟。 */
function formatDateTime(value: unknown): string {
    if (!value) return "-";
    return dateTimeFormat(String(value), DATETIME_PICKER_FORMAT);
}

/** 兼容 Vue 接口返回数组或 JSON 字符串的关联收款开票信息。 */
function parseRelatedReceiveInfo(value: unknown): RelatedReceiveInfoItem[] {
    if (Array.isArray(value)) return value as RelatedReceiveInfoItem[];
    if (typeof value !== "string" || !value.trim()) return [];
    try {
        const parsed: unknown = JSON.parse(value);
        return Array.isArray(parsed) ? (parsed as RelatedReceiveInfoItem[]) : [];
    } catch {
        return [];
    }
}

export interface TradeInvoiceDetailPageProps {
    loadDetail?: (id: number | string) => Promise<InvoiceVo>;
    invalidateInvoice?: (id: number | string, params: { reasons: string; fileId?: string }) => Promise<unknown>;
    previewInvoiceFile?: (fileId: number | string) => Promise<string | undefined>;
    permissions?: string[];
}

/** 复用 Vue 发票详情接口，保留历史详情页的核心数据契约。 */
export async function loadTradeInvoiceDetail(id: number | string): Promise<InvoiceVo> {
    const response = await request.service.get<InvoiceVo>(`/admin/invoice/vo/${id}`);
    return response.data;
}

/** 复用 Vue 发票作废接口，原因通过 query 参数提交，文件 ID 保留为可选契约。 */
export async function invalidateTradeInvoice(
    id: number | string,
    params: { reasons: string; fileId?: string }
): Promise<unknown> {
    const response = await request.service.put(`/admin/invoice/invalid/${id}`, undefined, { params });
    return response.data;
}

/** 查询 Vue 文件服务并返回发票单据预览地址。 */
export async function previewTradeInvoiceDetailFile(fileId: number | string): Promise<string | undefined> {
    const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${fileId}`);
    return response.data.path;
}

/** 将 Vue 发票状态统一为详情页文案使用的数字枚举。 */
function normalizeInvoiceDetail(result: InvoiceVo): InvoiceVo {
    // Vue 详情会以发票关联表中的开票金额为准，回填收款单展示字段。
    const relatedReceiveInfo = parseRelatedReceiveInfo(result.invoice.relatedReceiveInfo);
    return {
        ...result,
        invoice: {
            ...result.invoice,
            state: Number(result.invoice.state)
        },
        receivePayments: result.receivePayments.map(payment => {
            const related = relatedReceiveInfo.find(item => String(item.receivePaymentId) === String(payment.id));
            return {
                ...payment,
                invoicedAmount: related?.invoiceAmount ?? payment.invoicedAmount
            };
        })
    };
}

/** 发票详情页，先覆盖 Vue 详情接口、核心字段和关联收款单只读展示。 */
export function TradeInvoiceDetailPage({
    loadDetail = loadTradeInvoiceDetail,
    invalidateInvoice = invalidateTradeInvoice,
    previewInvoiceFile = previewTradeInvoiceDetailFile,
    permissions
}: TradeInvoiceDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 对齐 Vue 详情页的 v-auth，作废操作仅对发票处理权限开放。
    const canDeal = (permissions ?? storePermissions).includes("ROLE_INVOICE_DEAL");
    const invoiceId = new URLSearchParams(location.search).get("id") || "";
    const [detail, setDetail] = useState<InvoiceVo | null>(null);
    const [invalidateForm] = Form.useForm<{ reasons: string }>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [invalidateOpen, setInvalidateOpen] = useState(false);
    const [invalidateFileId, setInvalidateFileId] = useState<string>("");
    const [invalidateFileList, setInvalidateFileList] = useState<UploadUserFile[]>([]);
    const [invalidateError, setInvalidateError] = useState<string | null>(null);
    const [invalidateSuccess, setInvalidateSuccess] = useState(false);

    useEffect(() => {
        if (!invoiceId) {
            setErrorMessage("缺少发票编号");
            return;
        }
        let disposed = false;
        void loadDetail(invoiceId)
            .then(result => {
                if (!disposed) {
                    setDetail(normalizeInvoiceDetail(result));
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("发票详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [invoiceId, loadDetail]);

    const submitInvalidation = async () => {
        let values: { reasons: string };
        try {
            values = await invalidateForm.validateFields();
        } catch {
            return;
        }
        const reason = values.reasons.trim();
        try {
            await invalidateInvoice(invoiceId, {
                reasons: reason,
                ...(invalidateFileId ? { fileId: invalidateFileId } : {})
            });
            const refreshed = await loadDetail(invoiceId);
            setDetail(normalizeInvoiceDetail(refreshed));
            setInvalidateSuccess(true);
            setInvalidateOpen(false);
            invalidateForm.resetFields();
            setInvalidateFileId("");
            setInvalidateFileList([]);
            setInvalidateError(null);
        } catch {
            setInvalidateSuccess(false);
            setInvalidateError("发票作废失败");
        }
    };

    /** 对齐 Vue SingleUpload：作废凭证不超过 10MB，上传后展示项目并回填 fileId。 */
    const validateInvalidationFile = (file: File) => {
        if (file.size > 10 * 1024 * 1024) {
            setInvalidateError("作废文件最大不超过10M");
            return false;
        }
        setInvalidateError(null);
        return true;
    };

    const uploadInvalidationFile = async ({ file, onError, onSuccess }: UploadRequestOptions) => {
        const formData = new FormData();
        formData.append("file", file);
        try {
            // 按需加载文件服务，避免历史详情页的上传模块阻断路由测试和首屏加载。
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            if (!uploadedFile.id) throw new Error("上传响应缺少文件 ID");
            setInvalidateFileId(String(uploadedFile.id));
            setInvalidateFileList([
                {
                    name: uploadedFile.name || file.name,
                    response: uploadedFile,
                    url: uploadedFile.path
                }
            ]);
            setInvalidateError(null);
            onSuccess(uploadedFile);
        } catch {
            setInvalidateError("发票作废文件上传失败");
            onError(new Error("发票作废文件上传失败"));
        }
    };

    const closeInvalidationDialog = () => {
        setInvalidateOpen(false);
        invalidateForm.resetFields();
        setInvalidateFileId("");
        setInvalidateFileList([]);
        setInvalidateError(null);
    };

    const previewFile = async (fileId?: number) => {
        if (!fileId) return;
        try {
            const path = await previewInvoiceFile(fileId);
            if (path) window.open(path, "_blank");
        } catch {
            setErrorMessage("发票文件预览失败");
        }
    };

    const invoice = detail?.invoice;
    const order = detail?.order;
    const customer = order?.customerEnterprise;
    // 订单和采购用户沿用 Vue 详情页的历史跳转语义，避免详情页之间丢失业务上下文。
    const orderId = invoice?.orderId || order?.id;
    const customerId = order?.customerId;
    const customerLabel = customer ? `${customer.name || customer.realName || "-"} | ${customer.mobile || "-"}` : "-";
    // 关联收款单继续使用 Vue 的三个金额字段，交给 zhihao-ui-react Table 展示。
    const receivePaymentColumns: TableColumnProps<NonNullable<InvoiceVo["receivePayments"]>[number]>[] = [
        { label: "收款编号", minWidth: 148, prop: "id" },
        {
            label: "费用类型",
            minWidth: 92,
            prop: "paymentType",
            render: ({ row }) => paymentTypeLabels[Number(row.paymentType)] || "-"
        },
        { label: "收款金额", minWidth: 137, prop: "amount" },
        {
            label: "支付时间",
            minWidth: 150,
            prop: "payTime",
            render: ({ row }) => formatDate(row.payTime || row.payEndTime)
        },
        { label: "本次开票金额", minWidth: 137, prop: "invoicedAmount" }
    ];

    return (
        <section
            aria-label="trade-invoice-detail-page"
            className="detail-json-page detail-page-transparent admin-detail-page"
        >
            <div className="section-card admin-detail-header-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "发票编号", value: invoice?.id || invoiceId || "-" }]}
                    rightInfos={[{ label: "创建时间", value: formatDateTime(invoice?.createdTime) }]}
                    stateDesc={invoiceStateLabels[invoice?.state ?? 0] || "-"}
                    title="发票详情"
                />
            </div>
            {/* 保留旧详情编号的独立文本节点，兼容历史页面的定位语义且不增加可见内容。 */}
            {invoice?.id ? <span className="sr-only">{invoice.id}</span> : null}
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            {invalidateSuccess ? <Alert title="已作废" role="status" type="success" /> : null}
            {invoice ? (
                <div className="detail-json">
                    <section className="detail-json__block">
                        <div className="section-title">基本信息</div>
                        <ZhGrid className="admin-detail-grid" cols={4} gap={[20, 20]}>
                            <ZhBaseItem label="订单编号">
                                {orderId ? (
                                    <Link to={`/tradeManage/tradeOrder/detail?id=${orderId}`}>{orderId}</Link>
                                ) : (
                                    "-"
                                )}
                            </ZhBaseItem>
                            <ZhBaseItem label="采购用户" span={3}>
                                {customer && customerId ? (
                                    <Link to={`/lianYunUser/userInfo?userId=${customerId}`}>{customerLabel}</Link>
                                ) : (
                                    customerLabel
                                )}
                            </ZhBaseItem>
                            <ZhBaseItem label="实际收款金额" value={order?.actualReceivedAmount ?? "-"} />
                            <ZhBaseItem label="未开票金额" value={order?.uninvoicedAmount ?? "-"} />
                            <ZhBaseItem label="已开票金额" value={order?.invoicedAmount ?? "-"} />
                            <ZhBaseItem label="开票日期" value={formatDate(invoice.invoiceDate)} />
                        </ZhGrid>
                    </section>
                    <section className="detail-json__block">
                        <div className="flex items-center">
                            <div className="section-title">发票信息</div>
                            <span className="table-msg-tip">
                                开票总金额
                                <span>
                                    {invoice.amount == null
                                        ? "-"
                                        : new Intl.NumberFormat("en-US").format(invoice.amount)}
                                </span>
                            </span>
                        </div>
                        {detail?.receivePayments?.length ? (
                            <ZhTable
                                border
                                columns={receivePaymentColumns}
                                data={detail.receivePayments}
                                emptyText="暂无关联收款单"
                                pagination={false}
                                rowKey={row => String(row.id)}
                            />
                        ) : null}
                        <ZhGrid className="admin-detail-grid" cols={4} gap={[20, 20]}>
                            <ZhBaseItem label="发票单据" span={4}>
                                {invoice.fileId ? (
                                    <Button type="link" onClick={() => void previewFile(invoice.fileId)}>
                                        查看/下载发票单据
                                    </Button>
                                ) : (
                                    "-"
                                )}
                            </ZhBaseItem>
                        </ZhGrid>
                    </section>
                    {invoice.invalidReason ? (
                        <section className="detail-json__block">
                            <div className="section-title">作废信息</div>
                            <ZhGrid className="admin-detail-grid" cols={4} gap={[20, 20]}>
                                <ZhBaseItem label="作废时间" value={formatDateTime(invoice.invalidTime)} />
                                <ZhBaseItem label="作废原因" value={invoice.invalidReason} />
                                <ZhBaseItem label="作废文件" span={2}>
                                    {invoice.invalidFileId ? (
                                        <Button type="link" onClick={() => void previewFile(invoice.invalidFileId)}>
                                            查看/下载作废文件
                                        </Button>
                                    ) : (
                                        "-"
                                    )}
                                </ZhBaseItem>
                            </ZhGrid>
                        </section>
                    ) : null}
                </div>
            ) : null}
            <Space className="operation-fixed-btn" size={0}>
                {invoice?.state === 1 && canDeal ? (
                    <Button aria-label="作废发票" onClick={() => setInvalidateOpen(true)}>
                        作废
                    </Button>
                ) : null}
                <Button aria-label="返回" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </Space>
            <ZhDialog
                open={invalidateOpen}
                title="作废发票"
                width={400}
                onCancel={closeInvalidationDialog}
                footer={
                    <Space>
                        <Button onClick={closeInvalidationDialog}>取消</Button>
                        <Button onClick={() => void submitInvalidation()} type="primary">
                            提交
                        </Button>
                    </Space>
                }
            >
                {invalidateError ? (
                    <Alert className="mb-4" message={invalidateError} role="alert" type="error" />
                ) : null}
                <Form form={invalidateForm} labelCol={{ span: 5 }}>
                    <Form.Item
                        label="作废原因"
                        name="reasons"
                        rules={[
                            { required: true, whitespace: true, message: "请输入作废原因" },
                            { max: 200, message: "作废原因最多200个字符" }
                        ]}
                    >
                        <Input.TextArea
                            aria-label="作废原因"
                            maxLength={200}
                            placeholder="请输入作废原因"
                            rows={4}
                            showCount
                        />
                    </Form.Item>
                    <Form.Item className="mb-0" label="作废文件">
                        <label>
                            <span className="sr-only">作废文件</span>
                            {invalidateFileList.length ? (
                                <Space>
                                    <span>{invalidateFileList[0]?.name}</span>
                                    <Button
                                        htmlType="button"
                                        size="small"
                                        onClick={() => {
                                            setInvalidateFileId("");
                                            setInvalidateFileList([]);
                                        }}
                                    >
                                        删除
                                    </Button>
                                </Space>
                            ) : (
                                <ZhUpload
                                    action="#"
                                    accept="image/*,.pdf,.doc,.docx"
                                    limit={1}
                                    name="作废文件"
                                    beforeUpload={validateInvalidationFile}
                                    httpRequest={uploadInvalidationFile}
                                >
                                    <Button htmlType="button" type="link">
                                        <span
                                            aria-hidden="true"
                                            className="iconfont icon-a-Frame30"
                                            style={{ marginRight: 5 }}
                                        />
                                        上传文件
                                    </Button>
                                </ZhUpload>
                            )}
                        </label>
                        {!invalidateFileList.length ? (
                            <Typography.Text className="ml-4" type="secondary">
                                最大不超过10M
                            </Typography.Text>
                        ) : null}
                    </Form.Item>
                </Form>
            </ZhDialog>
        </section>
    );
}

export default TradeInvoiceDetailPage;
